import type {
  MonthlyStatus,
  PrescriptionCard,
  YearlyMonth,
} from '../api/performance.api';

/** "202607" → 7 */
export const monthOf = (ym: string) => Number(ym.slice(4, 6));
/** (2026, 7) → "202607" */
export const toYm = (year: number, month: number) =>
  `${year}${String(month).padStart(2, '0')}`;

/* ───────────── 월 카드 상태 (M-08) ───────────── */

export type MonthCardStatus = MonthlyStatus | 'NOT_ARRIVED';

/** 월 카드 배지 문구 — SETTLED는 "정산 비율 n%"라 화면에서 비율을 붙인다 */
export const MONTHLY_STATUS_LABEL: Record<MonthCardStatus, string> = {
  SETTLED: '정산 비율',
  PENDING_CONFIRMATION: '처방 확인중',
  BEFORE_SETTLEMENT: '정산 전',
  UNREGISTERED: '처방 미등록',
  NOT_ARRIVED: '미도래',
};

/** KPI 「정산 전 · 미등록」 — 응답 월 카드 중 정산 전 · 처방 미등록인 달 */
export const getPendingMonths = (months: YearlyMonth[]) =>
  months
    .filter(
      (m) =>
        m.monthlyStatusCode === 'BEFORE_SETTLEMENT' ||
        m.monthlyStatusCode === 'UNREGISTERED',
    )
    .map((m) => ({
      month: monthOf(m.settlementYearMonth),
      status: m.monthlyStatusCode,
    }));

/* ───────────── 추이 차트 (M-08 · M-09) ───────────── */

/** 차트 한 달 값 — 응답에 있는 달만 넘긴다 */
export interface TrendPoint {
  ym: string;
  estimated: number | null;
  /** 막대 — 정산이 끝난 달만 값이 있다 */
  confirmed: number | null;
  /** 처방 미등록 — 0 위치에 회색 점 */
  unregistered?: boolean;
}

/** 연간 월 카드 → 차트 값 (미등록 달은 회색 점, 정산 막대는 SETTLED 달만) */
export const toYearlyTrendPoints = (months: YearlyMonth[]): TrendPoint[] =>
  months.map((m) => {
    const unregistered = m.monthlyStatusCode === 'UNREGISTERED';
    return {
      ym: m.settlementYearMonth,
      estimated: unregistered ? 0 : m.estimatedAmountPrescription,
      confirmed:
        m.monthlyStatusCode === 'SETTLED' ? m.confirmedAmountSettlement : null,
      unregistered,
    };
  });

/* ───────────── 처방 카드 상태 배지 (M-09 · M-17 · M-18) ───────────── */

/** Figma 실적_의약품(712:4818) 배지 — Badge strong small의 state */
export type LineBadgeState = 'success' | 'warning' | 'error' | 'disabled';

export interface LineBadge {
  label: string;
  state: LineBadgeState;
}

type StatusKind =
  | 'APPROVED'
  | 'UNREGISTERED'
  | 'NON_TRADABLE'
  | 'MIN_AMOUNT'
  | 'ABSORPTION'
  | 'EDI';

/**
 * 상태 코드 · 문구 → 판정용 종류. 처방 승인 코드는 문서에 있는 4종이고,
 * 정산 상태 코드는 목록이 정해지지 않아 표시 문구(승인 · 정산불가 EDI · 흡수율 미달 …)로도 맞춘다.
 */
const kindOf = (
  code: string | null | undefined,
  label: string | null | undefined,
): StatusKind | null => {
  if (!code && !label) return null;
  const c = code ?? '';
  const l = label ?? '';
  if (c === 'APPROVED' || l === '승인') return 'APPROVED';
  if (c === 'UNREGISTERED_BUSINESS' || l.includes('미등록 거래처'))
    return 'UNREGISTERED';
  if (c === 'NON_TRADABLE_DRUG' || l.includes('거래 불가'))
    return 'NON_TRADABLE';
  if (c === 'BELOW_MINIMUM_AMOUNT' || l.includes('최소금액'))
    return 'MIN_AMOUNT';
  if (c.includes('EDI') || l.includes('EDI')) return 'EDI';
  if (c.includes('ABSORPTION') || l.includes('흡수율')) return 'ABSORPTION';
  return null;
};

const rxKind = (line: PrescriptionCard) =>
  kindOf(line.approvalStatusCode, line.approvalStatus);
const stKind = (line: PrescriptionCard) =>
  kindOf(line.settlementStatusCode, line.settlementStatus);

/**
 * 처방 카드 상태 배지 — 앱 판정 규칙을 그대로 옮겼다 (docs/api/performance.md 표).
 * 위에서부터 먼저 맞는 것 하나만 보여준다. 처방 · 정산이 모두 승인이거나 정산 전이면 배지가 없다
 * (Figma 실적_의약품 Type=기본 — 정산 전 카드는 정산 값이 "-"로만 보인다).
 * 최소금액 미달이어도 제약사가 승인하면 「제약사 승인」(Figma 712:4819 — 예상 금액 자리 "최소금액 기준 미달").
 */
export const getLineBadge = (line: PrescriptionCard): LineBadge | null => {
  const rx = rxKind(line);
  const st = stKind(line);
  const hasSettlement = !!(line.settlementStatusCode || line.settlementStatus);

  if (rx === 'NON_TRADABLE' || st === 'NON_TRADABLE')
    return { label: '거래 불가 의약품', state: 'disabled' };
  if (st === 'EDI') return { label: '정산 불가 EDI', state: 'disabled' };
  if (rx === 'UNREGISTERED' || st === 'UNREGISTERED')
    return { label: '미등록 거래처', state: 'error' };
  if (st === 'ABSORPTION') return { label: '흡수율 미달', state: 'warning' };
  if (rx === 'MIN_AMOUNT' && st === 'APPROVED')
    return { label: '제약사 승인', state: 'success' };
  if (rx === 'MIN_AMOUNT' || st === 'MIN_AMOUNT')
    return { label: '최소금액 미달', state: 'warning' };
  if (!hasSettlement) return null;
  if (rx !== 'APPROVED' && st === 'APPROVED')
    return { label: '제약사 승인', state: 'success' };
  return null;
};

/** 거래 불가 의약품은 카드 글자를 흐리게 보여준다 */
export const isLineDisabled = (line: PrescriptionCard) =>
  rxKind(line) === 'NON_TRADABLE' || stKind(line) === 'NON_TRADABLE';

/** 최소금액 미달로 예상 금액이 없으면 그 자리에 "최소금액 기준 미달"을 쓴다 */
export const isBelowMinimumEstimate = (line: PrescriptionCard) =>
  rxKind(line) === 'MIN_AMOUNT' &&
  (line.estimatedAmount === null || line.estimatedAmount === 0);

/* ───────────── 처방월 묶음 (M-09 의약품 탭) ───────────── */

export interface PrescriptionMonthGroup {
  /** 처방월 yyyyMM */
  ym: string;
  lines: PrescriptionCard[];
  estimated: number;
  confirmed: number;
}

/**
 * 처방월로 묶는다 — 최근 처방월이 위 (기획 변경: 처방년월 묶음 정렬).
 * 묶음 안 순서는 서버 정렬(사용자가 고른 정렬) 그대로 둔다.
 * 확정 금액은 같은 (처방월, 의약품) 정산 합이 카드마다 반복될 수 있어 의약품 코드로 한 번만 센다.
 * TODO(server): 처방월별 합계 필드가 없어 불러온 카드로 계산한다.
 */
export const groupByPrescriptionMonth = (
  lines: PrescriptionCard[],
): PrescriptionMonthGroup[] => {
  const map = new Map<string, PrescriptionCard[]>();
  lines.forEach((l) =>
    map.set(l.prescriptionYearMonth, [
      ...(map.get(l.prescriptionYearMonth) ?? []),
      l,
    ]),
  );
  return [...map]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([ym, rows]) => {
      const confirmedByDrug = new Map<string, number>();
      rows.forEach((r) => {
        if (r.confirmedAmount !== null)
          confirmedByDrug.set(
            r.drugCode + (r.drugName ?? ''),
            r.confirmedAmount,
          );
      });
      return {
        ym,
        lines: rows,
        estimated: rows.reduce((a, r) => a + (r.estimatedAmount ?? 0), 0),
        confirmed: [...confirmedByDrug.values()].reduce((a, v) => a + v, 0),
      };
    });
};

/* ───────────── 년월 ───────────── */

/** "202607" 기준 ±n달 */
export const shiftYm = (ym: string, diff: number) => {
  const d = new Date(Number(ym.slice(0, 4)), monthOf(ym) - 1 + diff, 1);
  return toYm(d.getFullYear(), d.getMonth() + 1);
};

/** "202607" → "2026년 7월" */
export const ymLabel = (ym: string) => `${ym.slice(0, 4)}년 ${monthOf(ym)}월`;

/** 증감률 % (기준 0 · 없음이면 null) */
export const changeRate = (current: number, base: number | null | undefined) =>
  base === null || base === undefined || base === 0
    ? null
    : ((current - base) / base) * 100;

/** 비율 표시 — 정수 반올림 (docs/api 공통 규칙) */
export const formatRatio = (v: number | null | undefined) =>
  v === null || v === undefined ? '-' : `${Math.round(v)}%`;

/** 증감 표시 — +12% / -8% */
export const formatChange = (v: number | null | undefined) =>
  v === null || v === undefined ? '-' : `${v > 0 ? '+' : ''}${Math.round(v)}%`;
